import React, { useEffect, useState } from 'react';
import { AudioStats } from '../utils/audioAnalysis';
import PracticeRecommendations from './PracticeRecommendations';
import { WordPhonemeAnalyzer, WordPhonemeScore } from '../utils/wordPhonemeAnalysis';
import AccentDiagnosticTable from './AccentDiagnosticTable';
import { getMultiAccentService, AccentRecognitionResult, ACCENT_CONFIGS } from '../utils/multiAccentRecognition';
import './PracticeSentenceFeedback.css';

interface PracticeSentenceFeedbackProps {
  stats: AudioStats;
  targetSentence?: string;
  audioUrl: string;
}

interface WordScore {
  word: string;
  expectedWord?: string;
  score: number;
  confidence: number;
  phonemeScore?: WordPhonemeScore;
}

const PracticeSentenceFeedback: React.FC<PracticeSentenceFeedbackProps> = ({ 
  stats, 
  targetSentence,
  audioUrl 
}) => {
  console.log('[PracticeSentenceFeedback] Component rendered with:', {
    hasStats: !!stats,
    hasTargetSentence: !!targetSentence,
    targetSentence,
    hasAudioUrl: !!audioUrl,
    transcription: stats?.transcription
  });
  
  const [wordPhonemeScores, setWordPhonemeScores] = useState<Map<string, WordPhonemeScore>>(new Map());
  const [accentResults, setAccentResults] = useState<AccentRecognitionResult[]>([]);
  const [showDiagnostic, setShowDiagnostic] = useState(true);
  const [diagnosticReady, setDiagnosticReady] = useState(false);
  const [selectedAccent, setSelectedAccent] = useState(
    localStorage.getItem('preferredAccent') || 'en-US'
  );
  
  useEffect(() => {
    // Simulate phoneme analysis for each word
    // In production, this would analyze actual audio segments
    const analyzer = new WordPhonemeAnalyzer();
    const scores = new Map<string, WordPhonemeScore>();
    
    const words = stats.transcription?.words || [];
    words.forEach((word, index) => {
      // Simulate phoneme scoring based on word and position
      // In reality, we'd extract audio segments for each word
      const simulatedScore: WordPhonemeScore = {
        word: word,
        // Simulate varying phoneme clarity scores
        phonemeClarity: 70 + Math.random() * 25,
        articulationScore: 65 + Math.random() * 30,
        spectralQuality: 60 + Math.random() * 35,
        issues: []
      };
      
      // Add simulated issues based on score
      if (simulatedScore.phonemeClarity < 75) {
        simulatedScore.issues.push('Some phonemes unclear');
      }
      if (simulatedScore.articulationScore < 70) {
        simulatedScore.issues.push('Could be more articulated');
      }
      
      scores.set(word.toLowerCase(), simulatedScore);
    });
    
    setWordPhonemeScores(scores);
  }, [stats.transcription]);
  
  // Simulate multi-accent recognition results
  useEffect(() => {
    console.log('[PracticeSentenceFeedback] AccentResults effect triggered');
    // In production, this would use actual multi-accent recognition
    // For now, simulate with variations of the transcription
    const simulateAccentResults = () => {
      console.log('[PracticeSentenceFeedback] Simulating accent results');
      const baseTranscript = stats.transcription?.transcript || '';
      const results: AccentRecognitionResult[] = [];
      
      // Common recognition errors for different accents
      const accentVariations: Record<string, (text: string) => string> = {
        'en-IN': (text) => text.replace(/th/gi, 'd').replace(/w/gi, 'v'),
        'en-PH': (text) => text.replace(/f/gi, 'p').replace(/v/gi, 'b'),
        'en-ZA': (text) => text.replace(/er/gi, 'ah'),
        'en-AU': (text) => text.replace(/ay/gi, 'ai'),
        'en-SG': (text) => text.replace(/th/gi, 't'),
      };
      
      ACCENT_CONFIGS.forEach(config => {
        let transcript = baseTranscript;
        let confidence = 0.5 + Math.random() * 0.3;
        
        // Apply accent-specific variations
        if (accentVariations[config.code] && Math.random() > 0.5) {
          transcript = accentVariations[config.code](transcript);
          confidence = Math.max(0.3, confidence - 0.3);
        }
        
        // Boost confidence for selected accent
        if (config.code === selectedAccent) {
          confidence = Math.min(0.95, confidence + 0.3);
          transcript = baseTranscript; // Reset to original
        } else if (config.code === 'en-US') {
          // US usually works well
          confidence = Math.min(0.9, confidence + 0.15);
        }
        
        // Some random word drops for low confidence
        if (confidence < 0.5 && transcript) {
          const words = transcript.split(' ');
          if (words.length > 2) {
            const dropIndex = Math.floor(Math.random() * words.length);
            words[dropIndex] = '...';
            transcript = words.join(' ');
          }
        }
        
        results.push({
          accent: config.name,
          flag: config.flag,
          transcript: transcript || '(no speech detected)',
          confidence: confidence,
          status: 'complete'
        });
      });
      
      setAccentResults(results);
      setDiagnosticReady(true);
    };
    
    // Start with pending state
    setAccentResults(ACCENT_CONFIGS.map(config => ({
      accent: config.name,
      flag: config.flag,
      transcript: '',
      confidence: 0,
      status: 'processing' as const
    })));
    
    // Simulate processing delay
    const timer = setTimeout(simulateAccentResults, 1500);
    return () => clearTimeout(timer);
  }, [stats.transcription, selectedAccent]);
  // Calculate word-level scores based on transcription confidence and alignment
  const calculateWordScores = (): WordScore[] => {
    // Get the spoken words from transcription
    const spokenWords = stats.transcription?.wordsWithTimestamps?.map(w => w.word) ||
                        stats.transcription?.words ||
                        stats.transcription?.transcript?.split(' ') || [];
    
    if (!targetSentence || spokenWords.length === 0) {
      // If no target sentence or no words detected, return basic scoring
      return spokenWords.map(word => ({
        word,
        score: 50, // Neutral score when we can't compare
        confidence: stats.transcription?.confidence || 0.5
      }));
    }

    // Prepare target words for comparison
    const targetWords = targetSentence
      .toLowerCase()
      .replace(/[.,!?;:'"]/g, '') // Remove punctuation
      .split(/\s+/) // Split by whitespace
      .filter(w => w.length > 0);

    // Use Levenshtein distance for fuzzy matching
    const calculateSimilarity = (word1: string, word2: string): number => {
      const w1 = word1.toLowerCase();
      const w2 = word2.toLowerCase();
      
      // Exact match
      if (w1 === w2) return 100;
      
      // Calculate Levenshtein distance
      const matrix: number[][] = [];
      for (let i = 0; i <= w2.length; i++) {
        matrix[i] = [i];
      }
      for (let j = 0; j <= w1.length; j++) {
        matrix[0][j] = j;
      }
      for (let i = 1; i <= w2.length; i++) {
        for (let j = 1; j <= w1.length; j++) {
          if (w2.charAt(i - 1) === w1.charAt(j - 1)) {
            matrix[i][j] = matrix[i - 1][j - 1];
          } else {
            matrix[i][j] = Math.min(
              matrix[i - 1][j - 1] + 1, // substitution
              matrix[i][j - 1] + 1,     // insertion
              matrix[i - 1][j] + 1      // deletion
            );
          }
        }
      }
      
      const distance = matrix[w2.length][w1.length];
      const maxLen = Math.max(w1.length, w2.length);
      const similarity = ((maxLen - distance) / maxLen) * 100;
      
      return Math.max(0, similarity);
    };

    // Score each spoken word based on position and similarity
    const wordScores: WordScore[] = [];
    let targetIndex = 0;
    
    for (let i = 0; i < spokenWords.length; i++) {
      const spokenWord = spokenWords[i];
      let score = 0;
      let confidence = 0.5;
      
      // Get confidence if available
      if (stats.transcription?.wordsWithTimestamps?.[i]) {
        confidence = stats.transcription.wordsWithTimestamps[i].confidence;
      }
      
      if (targetIndex < targetWords.length) {
        const expectedWord = targetWords[targetIndex];
        const similarity = calculateSimilarity(spokenWord, expectedWord);
        
        if (similarity >= 90) {
          // Excellent match - word is correct
          score = 95;
          const phonemeScore = wordPhonemeScores.get(spokenWord.toLowerCase());
          wordScores.push({
            word: spokenWord,
            expectedWord: expectedWord,
            score: Math.round(score * (0.7 + confidence * 0.3)),
            confidence,
            phonemeScore
          });
          targetIndex++; // Move to next expected word
        } else if (similarity >= 70) {
          // Good match - minor pronunciation issue
          score = 75;
          const phonemeScore = wordPhonemeScores.get(spokenWord.toLowerCase());
          wordScores.push({
            word: spokenWord,
            expectedWord: expectedWord,
            score: Math.round(score * (0.7 + confidence * 0.3)),
            confidence,
            phonemeScore
          });
          targetIndex++; // Still advance as it's close enough
        } else {
          // Check if this might be the next word (speaker skipped a word)
          let nextWordSimilarity = 0;
          let nextExpectedWord = '';
          if (targetIndex + 1 < targetWords.length) {
            nextExpectedWord = targetWords[targetIndex + 1];
            nextWordSimilarity = calculateSimilarity(spokenWord, nextExpectedWord);
          }
          
          if (nextWordSimilarity >= 80) {
            // Speaker likely skipped a word
            score = 60; // Penalize for skipping
            const phonemeScore = wordPhonemeScores.get(spokenWord.toLowerCase());
            wordScores.push({
              word: spokenWord,
              expectedWord: nextExpectedWord, // They said the next word
              score: Math.round(score * (0.7 + confidence * 0.3)),
              confidence,
              phonemeScore
            });
            targetIndex += 2; // Skip the missed word and move to next
          } else {
            // Wrong word entirely
            score = Math.max(20, similarity); // Heavy penalty but show similarity
            const phonemeScore = wordPhonemeScores.get(spokenWord.toLowerCase());
            wordScores.push({
              word: spokenWord,
              expectedWord: expectedWord, // Show what was expected
              score: Math.round(score * (0.7 + confidence * 0.3)),
              confidence,
              phonemeScore
            });
            // Don't advance targetIndex - still waiting for the correct word
            
            // Check if we should advance anyway (too many wrong words)
            if (i - targetIndex > 2) {
              targetIndex++; // Force advance to avoid getting stuck
            }
          }
        }
      } else {
        // Extra words beyond the target sentence
        score = 30; // Penalty for extra words
        const phonemeScore = wordPhonemeScores.get(spokenWord.toLowerCase());
        wordScores.push({
          word: spokenWord,
          expectedWord: undefined, // No expected word
          score: Math.round(score * (0.7 + confidence * 0.3)),
          confidence,
          phonemeScore
        });
      }
    }
    
    // Check for missing words at the end
    if (targetIndex < targetWords.length) {
      // Some target words were not spoken
      const missingCount = targetWords.length - targetIndex;
      // Reduce overall scores if significant words are missing
      if (missingCount > targetWords.length * 0.2) { // More than 20% missing
        wordScores.forEach(ws => {
          ws.score = Math.max(10, ws.score - 15); // Additional penalty
        });
      }
    }
    
    return wordScores;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 90) return '#22c55e'; // Excellent - green
    if (score >= 75) return '#84cc16'; // Good - lime
    if (score >= 60) return '#facc15'; // Fair - yellow
    if (score >= 40) return '#fb923c'; // Needs work - orange
    return '#ef4444'; // Poor - red
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Good';
    if (score >= 60) return 'Fair';
    if (score >= 40) return 'Needs Work';
    return 'Practice More';
  };

  const wordScores = calculateWordScores();
  const averageScore = wordScores.reduce((sum, w) => sum + w.score, 0) / Math.max(wordScores.length, 1);

  return (
    <div className="practice-sentence-feedback">
      <h2 className="feedback-title">Practice Sentence Results</h2>
      
      {/* Toggle for diagnostic view */}
      <div className="diagnostic-toggle">
        <button 
          className={`toggle-btn ${showDiagnostic ? 'active' : ''}`}
          onClick={() => setShowDiagnostic(!showDiagnostic)}
        >
          {showDiagnostic ? '📊 Hide Diagnostic' : '📊 Show Diagnostic'}
        </button>
      </div>
      
      {/* Audio Player */}
      <div className="audio-player">
        <audio controls src={audioUrl}>
          Your browser does not support the audio element.
        </audio>
      </div>
      
      {/* Accent Diagnostic Table */}
      {(() => {
        console.log('[PracticeSentenceFeedback] Diagnostic render check:', {
          showDiagnostic,
          accentResultsLength: accentResults.length,
          diagnosticReady,
          accentResults: accentResults.slice(0, 2)
        });
        return null;
      })()}
      {showDiagnostic && (
        <div className="diagnostic-section">
          {!diagnosticReady ? (
            <div className="diagnostic-loading">
              <p>🔄 Analyzing speech with multiple accent models...</p>
              <p className="loading-hint">This helps identify which accent setting works best for your voice.</p>
            </div>
          ) : accentResults.length > 0 ? (
            <AccentDiagnosticTable
              results={accentResults}
              targetSentence={targetSentence}
              recommendedAccent={selectedAccent}
            />
          ) : (
            <div className="diagnostic-error">
              <p>Unable to generate accent diagnostic data.</p>
            </div>
          )}
        </div>
      )}

      {/* Target Sentence Display */}
      {targetSentence && (
        <div className="target-sentence">
          <h3>Target Sentence:</h3>
          <p className="sentence-text">{targetSentence}</p>
        </div>
      )}

      {/* Overall Score */}
      <div className="overall-score">
        <div className="score-circle" style={{ borderColor: getScoreColor(averageScore) }}>
          <div className="score-value" style={{ color: getScoreColor(averageScore) }}>
            {Math.round(averageScore)}%
          </div>
          <div className="score-label">{getScoreLabel(averageScore)}</div>
        </div>
      </div>

      {/* Color-coded words */}
      <div className="word-feedback">
        <h3>Your Pronunciation:</h3>
        <div className="words-display">
          {wordScores.map((wordScore, index) => {
            const isMismatch = wordScore.expectedWord && 
                              wordScore.word.toLowerCase() !== wordScore.expectedWord.toLowerCase() &&
                              wordScore.score < 70;
            
            // Calculate combined score (transcription match + phoneme clarity)
            const transcriptionScore = wordScore.score;
            const phonemeClarity = wordScore.phonemeScore?.phonemeClarity || 50;
            const combinedScore = (transcriptionScore * 0.5 + phonemeClarity * 0.5);
            
            return (
              <div
                key={index}
                className="word-score-container"
                style={{ display: 'inline-block', margin: '8px', verticalAlign: 'top' }}
              >
                <div className="word-with-scores">
                  <span
                    className="scored-word"
                    style={{
                      backgroundColor: getScoreColor(combinedScore) + '20',
                      borderBottom: `3px solid ${getScoreColor(combinedScore)}`,
                      color: getScoreColor(combinedScore),
                      display: 'inline-block',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontWeight: '500',
                      fontSize: '1.1rem'
                    }}
                    title={`Combined Score: ${Math.round(combinedScore)}%`}
                  >
                    {wordScore.word}
                  </span>
                  
                  {/* Phoneme clarity indicator */}
                  {wordScore.phonemeScore && (
                    <div className="phoneme-clarity-indicator" style={{
                      marginTop: '4px',
                      fontSize: '0.7rem',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '4px',
                        marginBottom: '2px'
                      }}>
                        <span 
                          style={{
                            padding: '2px 6px',
                            borderRadius: '3px',
                            backgroundColor: getScoreColor(transcriptionScore) + '20',
                            border: `1px solid ${getScoreColor(transcriptionScore)}`,
                            color: getScoreColor(transcriptionScore)
                          }}
                          title="Text match score"
                        >
                          Text: {Math.round(transcriptionScore)}%
                        </span>
                        <span 
                          style={{
                            padding: '2px 6px',
                            borderRadius: '3px',
                            backgroundColor: getScoreColor(phonemeClarity) + '20',
                            border: `1px solid ${getScoreColor(phonemeClarity)}`,
                            color: getScoreColor(phonemeClarity)
                          }}
                          title="Phoneme clarity score"
                        >
                          Sound: {Math.round(phonemeClarity)}%
                        </span>
                      </div>
                      
                      {/* Show articulation score if available */}
                      {wordScore.phonemeScore.articulationScore && (
                        <div style={{
                          color: '#666',
                          fontSize: '0.65rem'
                        }}>
                          Articulation: {Math.round(wordScore.phonemeScore.articulationScore)}%
                        </div>
                      )}
                      
                      {/* Show issues if any */}
                      {wordScore.phonemeScore.issues && wordScore.phonemeScore.issues.length > 0 && (
                        <div style={{
                          color: '#d97706',
                          fontSize: '0.65rem',
                          fontStyle: 'italic',
                          marginTop: '2px'
                        }}>
                          {wordScore.phonemeScore.issues[0]}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {isMismatch && (
                    <div className="expected-word-hint" style={{
                      fontSize: '0.75rem',
                      color: '#666',
                      marginTop: '4px',
                      fontStyle: 'italic',
                      textAlign: 'center'
                    }}>
                      Expected: "{wordScore.expectedWord}"
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="score-legend">
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#22c55e' }}></span>
            <span>Excellent (90-100%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#84cc16' }}></span>
            <span>Good (75-89%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#facc15' }}></span>
            <span>Fair (60-74%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#fb923c' }}></span>
            <span>Needs Work (40-59%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ef4444' }}></span>
            <span>Practice More (&lt;40%)</span>
          </div>
        </div>
      </div>

      {/* Phoneme Practice Recommendations */}
      <div className="phoneme-recommendations">
        <PracticeRecommendations 
          phonemeStats={stats.phonemeStats}
          compact={true}
        />
      </div>

      {/* Quick Tips */}
      <div className="quick-tips">
        <h3>Quick Tips:</h3>
        <ul>
          {averageScore < 60 && (
            <li>Try speaking more slowly and clearly</li>
          )}
          {averageScore < 75 && (
            <li>Focus on pronouncing each word distinctly</li>
          )}
          {stats.estimatedWordsPerMinute > 180 && (
            <li>You're speaking quite fast - try slowing down a bit</li>
          )}
          {stats.estimatedWordsPerMinute < 100 && (
            <li>You can speak a bit faster while maintaining clarity</li>
          )}
          {stats.silenceRatio > 0.3 && (
            <li>Try to reduce pauses between words for smoother flow</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default PracticeSentenceFeedback;